// fetch-обёртка: baseUrl, Authorization, JSON, таймаут, парсинг ошибок, refresh токена на 401.

import type { AuthRefreshRequest, AuthRefreshResponse } from '@lunya/contracts'

import { API_BASE_URL, USE_MOCKS } from '~/shared/config'

import { resolveMock } from '../mock/registry'
import { buildUrl } from './buildUrl'
import { HttpError } from './HttpError'
import { tokenStorage } from './tokenStorage'
import { isApiErrorBody, type HttpMethod } from './types'

const REQUEST_TIMEOUT_MS = 20_000
const REFRESH_PATH = '/auth/refresh'

export type HttpRequestConfig<TInput = unknown> = {
  path: string | ((input: TInput) => string)
  method?: HttpMethod
  input?: TInput
  getSearchParams?: (input: TInput) => Record<string, unknown>
  getBody?: (input: TInput) => unknown
  /** Брать данные из реестра моков вместо сети, даже если глобальный флаг выключен */
  mocked?: boolean
  headers?: Record<string, string>
  signal?: AbortSignal
}

// Дедупликация параллельных рефрешей: пока один запрос обновляет токен, остальные ждут его же промис
let refreshPromise: Promise<boolean> | null = null

/**
 * Обновление пары токенов. Ручка говорит на snake_case (`AuthRefreshRequest` /
 * `AuthRefreshResponse` из контрактов) — раньше здесь уходил camelCase, ручка отвечала 422,
 * и через 15 минут (срок жизни access-токена) приложение молча оставалось без данных:
 * каждый запрос получал 401, а экраны рисовали пустоту без единой ошибки.
 */
async function performRefresh(): Promise<boolean> {
  const refreshToken = tokenStorage.getRefreshToken()
  if (!refreshToken) return false

  try {
    const request: AuthRefreshRequest = { refresh_token: refreshToken }

    const response = await fetch(`${API_BASE_URL}${REFRESH_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    })

    if (!response.ok) {
      tokenStorage.clearTokens()
      return false
    }

    const data = (await response.json()) as AuthRefreshResponse

    tokenStorage.setTokens({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
    })

    return true
  } catch {
    return false
  }
}

/**
 * Как заново войти, если refresh не помог (протух и он). Ставит `SessionProvider`: сам
 * `shared/api/auth` импортировать отсюда нельзя — он использует `httpRequest`, вышел бы цикл.
 */
let reauthenticate: (() => Promise<boolean>) | null = null

export function setReauthenticate(handler: (() => Promise<boolean>) | null): void {
  reauthenticate = handler
}

function ensureFreshToken(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = performRefresh()
      .then(async (isRefreshed) => (isRefreshed ? true : ((await reauthenticate?.()) ?? false)))
      .finally(() => {
        refreshPromise = null
      })
  }
  return refreshPromise
}

async function parseErrorBody(response: Response): Promise<{
  code?: string
  message?: string
  details?: unknown
}> {
  try {
    const body: unknown = await response.clone().json()
    if (isApiErrorBody(body)) {
      return body.error
    }
    return {}
  } catch {
    return {}
  }
}

function combineSignals(a: AbortSignal, b: AbortSignal | undefined): AbortSignal {
  if (!b) return a

  const controller = new AbortController()
  const abort = () => controller.abort()

  if (a.aborted || b.aborted) {
    controller.abort()
  } else {
    a.addEventListener('abort', abort)
    b.addEventListener('abort', abort)
  }

  return controller.signal
}

async function performFetch<TOutput, TInput>(
  url: string,
  method: HttpMethod,
  config: HttpRequestConfig<TInput>,
  isGet: boolean,
  isRetryAfterRefresh = false,
): Promise<TOutput> {
  const timeoutController = new AbortController()
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS)
  const signal = combineSignals(timeoutController.signal, config.signal)

  const headers: Record<string, string> = { ...config.headers }

  const bodyValue = isGet
    ? undefined
    : config.getBody
      ? config.getBody(config.input as TInput)
      : config.input

  if (!isGet && bodyValue !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const accessToken = tokenStorage.getAccessToken()
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  let response: Response

  try {
    response = await fetch(url, {
      method,
      headers,
      body: bodyValue !== undefined ? JSON.stringify(bodyValue) : undefined,
      signal,
    })
  } catch (error) {
    clearTimeout(timeoutId)

    if (timeoutController.signal.aborted) {
      throw new HttpError({
        status: 0,
        code: 'TIMEOUT',
        message: 'Превышено время ожидания ответа',
      })
    }

    throw new HttpError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: error instanceof Error ? error.message : 'Сетевая ошибка',
    })
  }

  clearTimeout(timeoutId)

  if (response.status === 401 && !isRetryAfterRefresh) {
    const refreshed = await ensureFreshToken()

    if (refreshed) {
      return performFetch<TOutput, TInput>(url, method, config, isGet, true)
    }
  }

  if (!response.ok) {
    const errorBody = await parseErrorBody(response)

    throw new HttpError({
      status: response.status,
      code: errorBody.code,
      message: errorBody.message ?? `Ошибка запроса: ${method} ${url} → ${response.status}`,
      details: errorBody.details,
    })
  }

  if (response.status === 204) {
    return undefined as TOutput
  }

  return (await response.json()) as TOutput
}

export async function httpRequest<TOutput, TInput = unknown>(
  config: HttpRequestConfig<TInput>,
): Promise<TOutput> {
  const method = config.method ?? 'GET'
  const isGet = method === 'GET'

  const path = typeof config.path === 'function' ? config.path(config.input as TInput) : config.path
  const { pathname } = buildUrl(path, config.input, { isGet: false })

  if (USE_MOCKS || config.mocked) {
    // Мок обязан видеть РОВНО то, что ушло бы по сети: для GET — входные параметры,
    // для остальных методов — тело после `getBody`. Иначе мутации с клиент→wire маппером
    // (например совместимость) получают в моке не те поля, что получил бы бэкенд,
    // и мок перестаёт быть честной заменой ручки.
    const mockPayload = isGet
      ? config.input
      : config.getBody
        ? config.getBody(config.input as TInput)
        : config.input

    return resolveMock<TOutput>(method, pathname, mockPayload)
  }

  const { search } = buildUrl(path, config.input, {
    isGet,
    getSearchParams: config.getSearchParams,
  })

  const url = `${API_BASE_URL}${pathname}${search}`

  return performFetch<TOutput, TInput>(url, method, config, isGet)
}
